import { makeStyles, tokens } from '@fluentui/react-components';
import { customPalette } from '../theme';

export const useTraceCalculationStyles = makeStyles({
    customTokens: {
        '--row-column-width': '117px',
        '--description-column-width': '246px',
        '--definition-column-width': '196px',
        '--amount-column-width': '100px',
        // Extra top padding applied to leaf row number cells
        '--leaf-row-number-extra-padding': '16px',
        '--trace-calculation-font-size': '12px',
        '--list-header-height': '26px',
        '--listcard-border-radius': '6px',
        '--listcard-box-shadow-margin': '6px',
        '--listcard-border-color': 'none',
        '--listcard-header-border-color': customPalette.statusCoolDarkGrey,
    },
    traceCalculationGrid: {
        display: 'flex',
        flexDirection: 'column',
        height: 'inherit',
    },
    columnHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        border: '1px solid var(--listcard-header-border-color)',
        marginLeft: '6px',
        marginRight: '4px',
        backgroundColor: tokens.colorNeutralBackground1,
        alignItems: 'center',
        fontSize: 'var(--trace-calculation-font-size)',
        fontStyle: 'normal',
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: '16px',
        alignContent: 'center',
        /* container should not add horizontal padding; individual header cells provide padding */
        padding: '0px 0px',
        minHeight: 'var(--list-header-height)',
    },
    // Padding class applied only to the "Total" header text
    totalHeaderText: {
        paddingLeft: '10px',
        display: 'flex',
        alignItems: 'center',
        height: 'var(--list-header-height)',
    },
    rowColumnHeader: {
        flex: 'var(--row-column-width) 1 1',
        padding: '0px 10px',
        height: 'var(--list-header-height)',
        display: 'flex',
        alignItems: 'center',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    descriptionColumnHeader: {
        flex: 'var(--description-column-width) 1 1',
        padding: '0px 10px',
        height: 'var(--list-header-height)',
        display: 'flex',
        alignItems: 'center',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    definitionColumnHeader: {
        flex: 'var(--definition-column-width) 1 1',
        padding: '0px 10px',
        height: 'var(--list-header-height)',
        display: 'flex',
        alignItems: 'center',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    amountColumnHeader: {
        flex: 'var(--amount-column-width) 0 0',
        padding: '0px 10px',
        textAlign: 'right',
        height: 'var(--list-header-height)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    treeRoot: {
        // Prevent Fluent's default hover from changing the background of every nested TreeItemLayout.
        // Keep backgrounds transparent here and let section header classes provide visible backgrounds.
        '& .fui-TreeItem > .fui-TreeItemLayout': {
            backgroundColor: 'transparent',
        },
        // Also explicitly neutralize hover/focus state backgrounds/colors applied to nested items
        '& .fui-TreeItem:hover > .fui-TreeItemLayout, & .fui-TreeItem:focus-within > .fui-TreeItemLayout, & .fui-TreeItem:active > .fui-TreeItemLayout':
            {
                backgroundColor: 'transparent',
                color: 'inherit',
            },
        // Ensure flex layout works properly with section containers that have overflow: hidden
        rowGap: '10px',
        overflowY: 'auto',
        scrollbarGutter: 'stable',
        width: '100%',
        height: 'calc(98% - var(--list-header-height))',
        paddingTop: '8px',
        paddingLeft: '6px',
        paddingRight: '4px',
        // Provide room at the bottom so section shadows can render without adding
        // margin to the final tree item. This preserves the flex layout.
        paddingBottom: 'var(--listcard-box-shadow-margin)',
    },
    treeNoGap: {
        paddingTop: '0px',
        rowGap: '0px',
    },
    // Total section styling (green status palette)
    totalSectionHeader: {
        backgroundColor: customPalette.statusDarkGreen,
        color: tokens.colorNeutralForegroundInverted,
        ':hover': {
            backgroundColor: customPalette.statusDarkGreen,
        },
    },
    totalSectionLayout: {
        paddingLeft: tokens.spacingHorizontalNone,
    },
    totalRowItem: {
        backgroundColor: customPalette.statusLightGreen,
        ':hover': {
            backgroundColor: customPalette.statusLightGreen,
        },
    },
    totalRowLayout: {
        paddingLeft: tokens.spacingHorizontalNone,
    },
    // Defining rows section styling (blue theme palette)
    definingSectionHeader: {
        backgroundColor: customPalette.themeLight,
        ':hover': {
            backgroundColor: customPalette.themeLight,
        },
    },
    // Rows impacted section styling (orange palette)
    impactedSectionHeader: {
        backgroundColor: customPalette.orangeLighter,
        ':hover': {
            backgroundColor: customPalette.orangeLighter,
        },
    },
    // Row data styling
    rowData: {
        display: 'flex',

        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    rowColumn: {
        flexBasis: 'var(--row-column-width)',
        flexGrow: 1,
        flexShrink: 1,
        fontWeight: tokens.fontWeightBold,
        height: 'var(--list-header-height)',
        lineHeight: 'var(--list-header-height)',
        padding: '0px 10px',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    rowNumberWrapper: {
        paddingLeft: `calc(var(--fluent-TreeItem--level, 0) * ${tokens.spacingHorizontalMNudge})`,
        display: 'flex',
        alignItems: 'center',
        maxWidth: '100%',
        overflow: 'hidden',
    },
    totalrowNumberWrapper: {
        paddingLeft: tokens.spacingHorizontalNone,
        display: 'flex',
        alignItems: 'center',
        maxWidth: '100%',
        overflow: 'hidden',
    },
    // For leaf nodes we add an extra left padding on top of the fluent level calc
    leafRowNumberWrapper: {
        paddingLeft: `calc(var(--fluent-TreeItem--level, 0) * ${tokens.spacingHorizontalMNudge} + var(--leaf-row-number-extra-padding))`,
        display: 'flex',
        alignItems: 'center',
        maxWidth: '100%',
        overflow: 'hidden',
    },

    rowNumberText: {
        display: 'flex',
        flex: '1 1 auto',
        maxWidth: 'calc(var(--row-column-width) - 36px)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    descriptionColumn: {
        display: 'flex',
        flexBasis: 'var(--description-column-width)',
        flexGrow: 1,
        flexShrink: 1,
        height: 'var(--list-header-height)',
        lineHeight: 'var(--list-header-height)',
        padding: '0px 10px',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    definitionColumn: {
        display: 'flex',
        flexBasis: 'var(--definition-column-width)',
        flexGrow: 1,
        flexShrink: 1,
        height: 'var(--list-header-height)',
        lineHeight: 'var(--list-header-height)',
        padding: '0px 10px',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    amountColumn: {
        display: 'flex',
        justifyContent: 'end',
        flexBasis: 'var(--amount-column-width)',
        flexGrow: 0,
        flexShrink: 0,
        textAlign: 'right',
        backgroundColor: tokens.colorNeutralBackground1,
        fontSize: 'var(--trace-calculation-font-size)',
        height: 'var(--list-header-height)',
        lineHeight: 'var(--list-header-height)',
        padding: '0px 10px',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    // Row styling for different levels
    defaultRowStyle: {
        backgroundColor: customPalette.neutralLighter,
        color: customPalette.neutralDark,
        fontSize: 'var(--trace-calculation-font-size)',
        fontStyle: 'normal',
        fontWeight: tokens.fontWeightMedium,
    },
    // Group header styling (for section headers)
    groupHeaderStyle: {
        color: customPalette.neutralDark,
        fontSize: 'var(--trace-calculation-font-size)',
        fontStyle: 'normal',
        fontWeight: tokens.fontWeightMedium,
    },
    // Group header layout used on section TreeItemLayout elements
    groupHeader: {
        height: 'var(--list-header-height)',
        display: 'flex',
        alignItems: 'center',
    },
    flexFullWidth: {
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
    },
    // Padding used to reserve space equal to the chevron icon width
    emptyRowPadding: {
        paddingLeft: tokens.spacingHorizontalXXL,
        display: 'flex',
        alignItems: 'center',
        height: 'var(--list-header-height)',
    },
    rightMargin: {
        marginRight: 'var(--listcard-box-shadow-margin)',
        marginLeft: 'var(--listcard-box-shadow-margin)',
    },
    topMargin: {
        marginTop: 'var(--listcard-box-shadow-margin)',
    },
    border: {
        border: `1px solid var(--listcard-border-color)`,
        borderRadius: 'var(--listcard-border-radius)',
        boxShadow: '0px 0.3px 0.9px 0px rgba(0, 0, 0, 0.10), 0px 1.6px 3.6px 0px rgba(0, 0, 0, 0.13)',
        overflow: 'hidden',
        minHeight: 'fit-content',
    },
    treeItemLayoutMain: {
        padding: tokens.spacingHorizontalNone,
    },
    chevronIcon: {
        transition: 'transform 0.1s ease',
        cursor: 'pointer',
        marginRight: '4px',
        transform: 'rotate(0deg)',
    },
    chevronIconOpen: {
        transform: 'rotate(90deg)',
    },
});
